import React, { useState, useEffect } from 'react';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { BsTelephone as Phone } from 'react-icons/bs';
import { TableCSS } from '../styles/MembersStyled';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { connect } from 'react-redux';

const Component = ({
    contacts = [],
    presets = [],
    labels = [],
    loading = false,
    onCreateContact,
    onCreateCall,
    onInitiateCall,
}) => {
    /* ------------------- state ------------------- */
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [bulkPresetId, setBulkPresetId] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    /* per-row preset + bulk modal */
    const [rowPresetById, setRowPresetById] = useState({});
    /* dialing state */
    const [perRowDialing, setPerRowDialing] = useState({});
    const [bulkDialing, setBulkDialing] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkCsvText, setBulkCsvText] = useState('');
    const [bulkSaving, setBulkSaving] = useState(false);

    /* add-contact modal state */
    const [addForm, setAddForm] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        labels: [],
        notes: '',
    });
    const [addSaving, setAddSaving] = useState(false);
    const [addErrors, setAddErrors] = useState({});
    useEffect(() => {
        // normalise list (filter(Boolean) removes null/undefined items)
        const list = Array.isArray(contacts) ? contacts.filter(Boolean) : [];
        if (!searchTerm) return setFilteredContacts(list);

        const lower = searchTerm.toLowerCase();
        setFilteredContacts(
            list.filter((c) => {
                const full = c.name?.toLowerCase() || '';
                return full.includes(lower) || (c.phone || '').includes(lower);
            })
        );
    }, [searchTerm, contacts]);

    /* ------------------- helpers ------------------- */
    const nameOf = (c) => `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name || 'Unnamed';
    const isE164 = (v) => /^\+[1-9]\d{1,14}$/.test(v || '');

    /* ------------------- handlers ------------------- */
    const toggleSelect = (id) =>
        setSelectedContacts((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const handleBulkDial = async () => {
        if (bulkDialing) return;
        if (selectedContacts.length === 0) {
            toast.error('Select at least one contact');
            return;
        }
        if (!bulkPresetId) {
            toast.error('Select a preset first');
            return;
        }

        setBulkDialing(true);
        try {
            // create calls
            const createResults = await Promise.allSettled(
                selectedContacts.map((cid) => onCreateCall({ contact: cid, preset: bulkPresetId }))
            );

            const successfulCreates = createResults
                .map((r, idx) => (r.status === 'fulfilled' ? { call: r.value, idx } : null))
                .filter(Boolean);

            // initiate the successfully created calls
            const initiateResults = await Promise.allSettled(
                successfulCreates.map(({ call }) => onInitiateCall(call.id || call._id))
            );

            const ok = initiateResults.filter((r) => r.status === 'fulfilled').length;
            const failed = selectedContacts.length - ok;

            toast.info(`${ok} initiated, ${failed} failed`);

            // clear only those contacts that succeeded
            if (ok > 0) {
                setSelectedContacts((prev) =>
                    prev.filter((_, i) => {
                        // keep only failed ones
                        const cr = createResults[i];
                        const ir = initiateResults[i];
                        return cr?.status !== 'fulfilled' || ir?.status !== 'fulfilled';
                    })
                );
            }
        } catch (err) {
            toast.error(err.message || 'Bulk dial failed');
        } finally {
            setBulkDialing(false);
        }
    };

    /* add-contact submit */
    const handleAddSubmit = async (e, { stayOpen = false } = {}) => {
        e?.preventDefault?.();
        const errors = {};
        if (!isE164(addForm.phone)) {
            errors.phone = 'Use E.164 format, e.g. +15551234567';
        }
        if (addForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email)) {
            errors.email = 'Enter a valid email or leave blank';
        }
        setAddErrors(errors);
        if (Object.keys(errors).length) return;

        try {
            setAddSaving(true);
            await onCreateContact({ ...addForm });
            toast.success('Contact added');
            if (stayOpen) {
                setAddForm({
                    firstName: '',
                    lastName: '',
                    phone: '',
                    email: '',
                    labels: [],
                    notes: '',
                });
                setAddErrors({});
            } else {
                setShowAddModal(false);
            }
        } catch (err) {
            if (err?.message?.toLowerCase?.().includes('duplicate') || err?.statusCode === 409) {
                setAddErrors((prev) => ({
                    ...prev,
                    phone: 'This phone already exists in your team',
                }));
            }
        } finally {
            setAddSaving(false);
        }
    };

    return (
        <div className="to-be-called-tab">
            {/* toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex">
                    <InputGroup className="me-2" style={{ width: 300 }}>
                        <input
                            className="input"
                            onChange={(e) => setSearchTerm(e.target.value)}
                            value={searchTerm}
                            placeholder="Search contacts/phones..."
                            style={{
                                width: '250px',
                                border: '1px solid #ccc',
                                borderRadius: 6,
                                padding: '6px 10px',
                            }}
                        />
                    </InputGroup>
                </div>
            </div>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            ) : filteredContacts.length === 0 ? (
                <div className="text-center text-muted">No contacts found</div>
            ) : (
                <>
                    <div className="custom-card ">
                        <TableCSS>
                            <thead>
                                <tr>
                                    <th />
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Preset</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredContacts
                                    .filter((c) => c && (c.id || c._id))
                                    .map((c) => {
                                        const cid = c.id || c._id; // fallback to _id
                                        return (
                                            <tr key={cid}>
                                                <td>
                                                    <Form.Check
                                                        checked={selectedContacts.includes(cid)}
                                                        onChange={() => toggleSelect(cid)}
                                                    />
                                                </td>
                                                <td>{nameOf(c)}</td>
                                                <td>{c.phone}</td>
                                                <td>
                                                    <Form.Control
                                                        as="select"
                                                        size="sm"
                                                        value={rowPresetById[cid] ?? ''}
                                                        onChange={(e) =>
                                                            setRowPresetById((m) => ({
                                                                ...m,
                                                                [cid]: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="">Select preset…</option>
                                                        {presets.map((p) => (
                                                            <option key={p.id || p._id} value={p.id || p._id}>
                                                                {p.title}
                                                            </option>
                                                        ))}
                                                    </Form.Control>
                                                </td>

                                                <td>
                                                    <Button
                                                        variant="outline-primary"
                                                        // disabled={!rowPresetById[cid] || perRowDialing[cid]}
                                                        onClick={async () => {
                                                            const presetId = rowPresetById[cid];
                                                            if (!presetId) {
                                                                toast.error('Select a preset first');
                                                                return;
                                                            }
                                                            setPerRowDialing((m) => ({ ...m, [cid]: true }));
                                                            try {
                                                                const call = await onCreateCall({
                                                                    contact: cid,
                                                                    preset: presetId,
                                                                });
                                                                await onInitiateCall(call.id || call._id);
                                                            } catch (e) {
                                                                // toast.error(e?.message || 'Failed to initiate call');
                                                            } finally {
                                                                setPerRowDialing((m) => ({ ...m, [cid]: false }));
                                                            }
                                                        }}
                                                    >
                                                        {perRowDialing[cid] ? (
                                                            <>
                                                                <Spinner
                                                                    size="sm"
                                                                    animation="border"
                                                                    className="me-1"
                                                                />{' '}
                                                                Calling…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Phone className="me-1" /> Call
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </TableCSS>
                    </div>

                    {/* bulk actions */}
                    <div className="d-flex align-items-center mt-3">
                        <Form.Control
                            as="select"
                            style={{ maxWidth: 220 }}
                            className="me-2"
                            value={bulkPresetId}
                            onChange={(e) => setBulkPresetId(e.target.value)}
                        >
                            <option value="">Preset for bulk dial…</option>
                            {presets.map((p) => (
                                <option key={p.id || p._id} value={p.id || p._id}>
                                    {p.title}
                                </option>
                            ))}
                        </Form.Control>
                        <Button
                            variant="primary"
                            disabled={selectedContacts.length === 0 || !bulkPresetId || bulkDialing}
                            onClick={handleBulkDial}
                            className="ml-2"
                        >
                            {bulkDialing ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-1" /> Calling...
                                </>
                            ) : (
                                <>
                                    <Phone className="me-1" /> Call Selected ({selectedContacts.length})
                                </>
                            )}
                        </Button>
                    </div>
                </>
            )}

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Contact</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSubmit}>
                    <Modal.Body>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>First name</Form.Label>
                                    <Form.Control
                                        value={addForm.firstName}
                                        onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                                        placeholder="Jane"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Last name</Form.Label>
                                    <Form.Control
                                        value={addForm.lastName}
                                        onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                                        placeholder="Doe"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Company</Form.Label>
                                    <Form.Control
                                        value={addForm.company}
                                        onChange={(e) => setAddForm({ ...addForm, company: e.target.value })}
                                        placeholder="Acme Health"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Phone (E.164)</Form.Label>
                                    <Form.Control
                                        value={addForm.phone}
                                        onChange={(e) =>
                                            setAddForm({
                                                ...addForm,
                                                phone: e.target.value.trim(),
                                            })
                                        }
                                        placeholder="+15551234567"
                                    />
                                    {addErrors.phone && (
                                        <div className="invalid-feedback d-block">{addErrors.phone}</div>
                                    )}
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={addForm.email}
                                        onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                                        placeholder="jane@example.com"
                                    />
                                    {addErrors.email && (
                                        <div className="invalid-feedback d-block">{addErrors.email}</div>
                                    )}
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Labels</Form.Label>
                                    <Form.Control
                                        as="select"
                                        multiple
                                        value={addForm.labels}
                                        onChange={(e) => {
                                            const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                                            setAddForm({ ...addForm, labels: opts });
                                        }}
                                    >
                                        {labels.map((l) => (
                                            <option key={l.id || l._id} value={l.id || l._id}>
                                                {l.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </div>
                            <div className="col-12">
                                <Form.Group>
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={addForm.notes}
                                        onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                                        placeholder="Any context for this contact"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={addSaving}>
                            Cancel
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={(e) => handleAddSubmit(e, { stayOpen: true })}
                            disabled={addSaving}
                        >
                            Save &amp; Add Another
                        </Button>
                        <Button variant="primary" type="submit" disabled={addSaving}>
                            {addSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* bulk-upload modal */}
            <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Bulk Upload Contacts</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-2">
                        Paste CSV with headers: <code>firstName,lastName,company,phone,email,notes</code>
                    </p>
                    <Form.Control
                        as="textarea"
                        rows={10}
                        value={bulkCsvText}
                        onChange={(e) => setBulkCsvText(e.target.value)}
                        placeholder={
                            'firstName,lastName,company,phone,email,notes\nJane,Doe,Acme,+15551234567,jane@example.com,VIP lead'
                        }
                    />
                    <div className="mt-2 small text-muted">
                        Phone must be in E.164 format (e.g. +15551234567). Rows with invalid phone will be skipped.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBulkModal(false)} disabled={bulkSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={bulkSaving || !bulkCsvText.trim()}
                        onClick={async () => {
                            // Normalize all possible line-break variants (CRLF, CR, LF, unicode)
                            // and convert literal “\n” / “\r” escape sequences coming from some paste sources.
                            const normalized = bulkCsvText
                                // literal escapes first
                                .replace(/\\r\\n/g, '\n')
                                .replace(/\\n/g, '\n')
                                .replace(/\\r/g, '\n')
                                // actual CRLF / CR to LF
                                .replace(/\r\n/g, '\n')
                                .replace(/\r/g, '\n')
                                // unicode separators
                                .replace(/[\u2028\u2029]/g, '\n');

                            let lines = normalized
                                .split(/\n/)
                                .map((l) => l.trimEnd())
                                .filter((l) => l.trim().length);

                            // Handle case where header and first row are on the same line
                            const expectedHeader = 'firstName,lastName,company,phone,email,notes';
                            const firstLine = lines[0] || '';
                            if (
                                firstLine.toLowerCase().startsWith(expectedHeader.toLowerCase()) &&
                                firstLine.length > expectedHeader.length
                            ) {
                                const restInline = firstLine.slice(expectedHeader.length).trim().replace(/^\s+/, ''); // remove leading whitespace
                                lines = [expectedHeader, ...(restInline ? [restInline] : []), ...lines.slice(1)];
                            }

                            // Need header + at least one data row
                            if (lines.length < 2) {
                                toast.error('Add at least one data row on a new line under the header');
                                return;
                            }

                            // split helper – handles quoted CSV / tab / semicolon
                            const splitCSV = (row) => {
                                if (row.includes(',')) {
                                    const re = /,(?=(?:[^"]*"[^"]*")*[^"]*$)/;
                                    return row.split(re).map((s) => s.replace(/^"|"$/g, '').trim());
                                }
                                if (row.includes('\t')) return row.split('\t').map((s) => s.trim());
                                if (row.includes(';')) return row.split(';').map((s) => s.trim());
                                return row.split(',').map((s) => s.trim());
                            };

                            const header = splitCSV(lines[0]).map((h) => h.trim());
                            const idx = (name) => header.findIndex((h) => h.toLowerCase() === name.toLowerCase());
                            const iFirst = idx('firstname');
                            const iLast = idx('lastname');
                            const iComp = idx('company');
                            const iPhone = idx('phone');
                            const iEmail = idx('email');
                            const iNotes = idx('notes');
                            if (iPhone === -1) {
                                toast.error('CSV must include phone header');
                                return;
                            }

                            const isE164 = (v) => /^\+[1-9]\d{1,14}$/.test(v || '');
                            let ok = 0,
                                skipped = 0;
                            setBulkSaving(true);
                            try {
                                for (let li = 1; li < lines.length; li++) {
                                    const cols = splitCSV(lines[li]);
                                    const contact = {
                                        firstName: iFirst > -1 ? (cols[iFirst] || '').trim() : '',
                                        lastName: iLast > -1 ? (cols[iLast] || '').trim() : '',
                                        phone: iPhone > -1 ? (cols[iPhone] || '').trim() : '',
                                        email: iEmail > -1 ? (cols[iEmail] || '').trim() : '',
                                        notes: iNotes > -1 ? (cols[iNotes] || '').trim() : '',
                                    };
                                    if (!isE164(contact.phone)) {
                                        skipped++;
                                        continue;
                                    }
                                    try {
                                        await onCreateContact(contact);
                                        ok++;
                                    } catch (e) {
                                        skipped++;
                                    }
                                }
                                if (ok === 0 && skipped > 0) {
                                    toast.warn(
                                        `Imported 0 contact(s). Skipped ${skipped}. Check phone format and headers.`
                                    );
                                } else {
                                    toast.success(`Imported ${ok} contact(s). Skipped ${skipped}.`);
                                }
                                setShowBulkModal(false);
                                setBulkCsvText('');
                            } finally {
                                setBulkSaving(false);
                            }
                        }}
                    >
                        {bulkSaving ? 'Importing…' : 'Import'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({
    contacts: (state.callCenter.contacts || []).filter((c) => c?.status === 'active'),
    presets: state.callCenter?.presets || [],
});
const mapDispatchToProps = {};
const ToBeCalledTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(ToBeCalledTab);
